import React, { useEffect, useMemo, useState } from "react";
import { AxiosResponse } from "axios";
import { getRequest, putRequest } from "../../../utils/httpRequest";
import { Badge } from "../../../stores/BadgeStore/Types";
import { CourseResource } from "../../../stores/CoursesStore/Types";
import BadgeCard from "./BadgeCard";
import BadgeDialog from "./BadgeDialog";
import BadgeProgress from "./BadgeProgress";
import EmptyCourseBadges from "./EmptyCourseBadges";
import LockedCourseCard from "./LockedCourseCard";
import {
  BadgeDialogContent,
  CourseBadgeGroup,
  badgeDialogContent,
  courseImageUrl,
  dashboardCourseImageUrl,
  earnedBadges,
  lockedCourseDialogContent,
} from "./helpers";

const NewBadges: React.FC = () => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [allCourses, setAllCourses] = useState<CourseResource[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showAllSystem, setShowAllSystem] = useState(false);
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [courseSearch, setCourseSearch] = useState("");
  const [openCourseIds, setOpenCourseIds] = useState<(number | string)[]>([]);
  const [dialogContent, setDialogContent] = useState<BadgeDialogContent | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      getRequest("/api/pages/badges.json"),
      getRequest("/api/dashboard_courses/profile.json"),
    ]).then(([badgesResponse, coursesResponse]) => {
      setBadges((badgesResponse as AxiosResponse).data);
      setAllCourses((coursesResponse as AxiosResponse).data);
      setLoaded(true);
    });
  }, []);

  const systemBadges = useMemo(
    () => badges.filter((badge) => !badge.scoped),
    [badges],
  );

  const courseBadgeGroups = useMemo(() => {
    const groups = new Map<number | string, CourseBadgeGroup>();

    badges
      .filter((badge) => badge.scoped && badge.enrolment?.course)
      .forEach((badge) => {
        const course = badge.enrolment.course;
        const courseId = course.id || course.slug || badge.name;

        if (!groups.has(courseId)) {
          groups.set(courseId, {
            courseId,
            courseName: course.name,
            courseImageUrl: courseImageUrl(badge),
            badges: [],
          });
        }

        groups.get(courseId).badges.push(badge);
      });

    return Array.from(groups.values()).sort((a, b) =>
      a.courseName.localeCompare(b.courseName),
    );
  }, [badges]);

  const systemEarnedCount = earnedBadges(systemBadges).length;
  const courseBadges = courseBadgeGroups.reduce(
    (items, group) => items.concat(group.badges),
    [] as Badge[],
  );
  const courseEarnedCount = earnedBadges(courseBadges).length;
  const lockedCourseBadgeCount = Math.max(
    4,
    ...courseBadgeGroups.map((group) => group.badges.length),
  );
  const filteredAllCourses = allCourses.filter((course) =>
    course.name.toLowerCase().includes(courseSearch.trim().toLowerCase()),
  );
  const visibleSystemBadges = showAllSystem
    ? systemBadges
    : systemBadges.slice(0, 4);
  const toggleOpenCourse = (courseId: number | string) => {
    setOpenCourseIds((currentIds) =>
      currentIds.includes(courseId)
        ? currentIds.filter((id) => id !== courseId)
        : currentIds.concat(courseId),
    );
  };

  const markAsOpened = (badge: Badge) => {
    putRequest(`/api/achievements/badges/${badge.id}.json`, {
      achievement: { opened_badge: true },
    }).then((response: AxiosResponse) => {
      setBadges((currentBadges) =>
        currentBadges.map((item) =>
          item.name === badge.name ? response.data : item,
        ),
      );
    });
  };

  const openBadge = (badge: Badge) => {
    setDialogContent(badgeDialogContent(badge));
    setModalOpen(true);
    if (badge.persisted && !badge.opened) markAsOpened(badge);
  };

  const openLockedCourse = (course: CourseResource) => {
    setDialogContent(lockedCourseDialogContent(course));
    setModalOpen(true);
  };

  const closeDialog = () => setModalOpen(false);

  return (
    <main id="main" className="facelift-badges">
      {!loaded && (
        <div className="ui active inverted dimmer">
          <div className="ui text loader">Loading</div>
        </div>
      )}

      <section className="bd-hero" aria-labelledby="bd-title">
        <div className="bd-bg" aria-hidden="true">
          <svg
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
          >
            <svg x="86%" y="-20%" overflow="visible">
              <use
                href="#blob-1"
                fill="#B798F2"
                transform="scale(1.3)"
                opacity="0.22"
              />
            </svg>
            <svg x="-4%" y="55%" overflow="visible">
              <use
                href="#blob-2"
                fill="#FFA5BC"
                transform="scale(1)"
                opacity="0.14"
              />
            </svg>
          </svg>
        </div>
        <span className="big-42" aria-hidden="true">
          42
        </span>
        <div className="container">
          <h1 id="bd-title">Your Badge Collection</h1>
          <p className="bd-subtitle">Gold stars for grown-ups.</p>
        </div>
      </section>

      <section className="bd-group" id="sg-system" aria-labelledby="sys-title">
        <div className="container">
          <div className="bd-group-head">
            <h2 id="sys-title">42courses badges</h2>
            <BadgeProgress
              earned={systemEarnedCount}
              total={systemBadges.length}
              label="42courses badges earned"
            />
          </div>

          <div className="bd-grid" id="systemGrid">
            {visibleSystemBadges.map((badge) => (
              <BadgeCard badge={badge} onOpen={openBadge} key={badge.name} />
            ))}
          </div>

          {systemBadges.length > 4 && (
            <div className="bd-showall-row">
              <button
                className="bd-showall"
                type="button"
                aria-expanded={showAllSystem}
                onClick={() => setShowAllSystem(!showAllSystem)}
              >
                <span className="bd-showall-label">
                  {showAllSystem
                    ? "Show fewer 42courses badges"
                    : "Show all 42courses badges"}
                </span>
                <svg
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 5 L6 8 L9 5" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </section>

      <section
        className="bd-group alt"
        id="sg-courses"
        aria-labelledby="cb-title"
      >
        <div className="container">
          <div className="bd-group-head">
            <h2 id="cb-title">Mini MBA badges</h2>
            <BadgeProgress
              earned={courseEarnedCount}
              total={courseBadges.length}
              label="course badges earned"
            />
          </div>

          {courseBadgeGroups.length > 0 ? (
            <div id="workingOnTier">
              <p className="bd-subhead">Courses you're working on</p>
              {courseBadgeGroups.map((group) => {
                const earned = earnedBadges(group.badges).length;
                const complete = earned === group.badges.length;

                return (
                  <div className="bd-course" key={group.courseId}>
                    <div className="bd-course-head">
                      <div className="bd-course-thumb">
                        {group.courseImageUrl && (
                          <img
                            src={group.courseImageUrl}
                            alt=""
                            loading="lazy"
                          />
                        )}
                      </div>
                      <div>
                        <div className="bd-course-title">
                          {group.courseName}
                        </div>
                      </div>
                      <span
                        className={`bd-course-count ${
                          complete ? "complete" : ""
                        }`}
                      >
                        {earned} / {group.badges.length}
                      </span>
                    </div>
                    <div className="bd-grid">
                      {group.badges.map((badge) => (
                        <BadgeCard
                          badge={badge}
                          onOpen={openBadge}
                          key={badge.name}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            loaded && <EmptyCourseBadges />
          )}

          {allCourses.length > 0 && (
            <>
              <div className="bd-showall-row" id="allCoursesToggleRow">
                <button
                  className="bd-showall"
                  type="button"
                  aria-expanded={showAllCourses}
                  aria-controls="allCoursesWrap"
                  onClick={() => setShowAllCourses(!showAllCourses)}
                >
                  <span className="bd-showall-label">
                    {showAllCourses
                      ? "Hide all courses"
                      : `Show all ${allCourses.length} courses`}
                  </span>
                  <svg
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 5 L6 8 L9 5" />
                  </svg>
                </button>
              </div>

              {showAllCourses && (
                <div className="bd-allcourses" id="allCoursesWrap">
                  <p className="bd-subhead">All courses</p>
                  <div className="bd-allcourses-bar">
                    <div className="bd-search">
                      <span className="search-ic" aria-hidden="true">
                        <svg viewBox="0 0 16 16">
                          <circle cx="7" cy="7" r="5" />
                          <line
                            x1="11"
                            y1="11"
                            x2="15"
                            y2="15"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                      <input
                        type="search"
                        placeholder="Filter courses by name"
                        aria-label="Filter courses by name"
                        autoComplete="off"
                        value={courseSearch}
                        onChange={(event) =>
                          setCourseSearch(event.target.value)
                        }
                      />
                    </div>
                    <span className="bd-allcourses-note" id="searchNote">
                      {filteredAllCourses.length}{" "}
                      {filteredAllCourses.length === 1 ? "course" : "courses"}
                    </span>
                  </div>

                  <div className="bd-acc" id="courseAccordion">
                    {filteredAllCourses.map((course) => {
                      const courseId = course.id || course.slug;
                      const open = openCourseIds.includes(courseId);

                      return (
                        <div
                          className={`bd-acc-item ${open ? "is-open" : ""}`}
                          data-name={course.name.toLowerCase()}
                          key={courseId}
                        >
                          <button
                            className="bd-acc-trigger"
                            type="button"
                            aria-expanded={open}
                            onClick={() => toggleOpenCourse(courseId)}
                          >
                            <span className="bd-acc-thumb">
                              {dashboardCourseImageUrl(course) && (
                                <img
                                  src={dashboardCourseImageUrl(course)}
                                  alt=""
                                  loading="lazy"
                                />
                              )}
                            </span>
                            <span className="bd-acc-title">{course.name}</span>
                            <span className="bd-course-count none">
                              0 / {lockedCourseBadgeCount}
                            </span>
                            <span className="bd-acc-chevron" aria-hidden="true">
                              <svg viewBox="0 0 12 12">
                                <path d="M3 5 L6 8 L9 5" />
                              </svg>
                            </span>
                          </button>
                          <div className="bd-acc-panel">
                            <div className="bd-grid">
                              {Array.from({
                                length: lockedCourseBadgeCount,
                              }).map((_, index) => (
                                <LockedCourseCard
                                  course={course}
                                  onOpen={openLockedCourse}
                                  key={index}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {filteredAllCourses.length === 0 && (
                      <div className="bd-acc-empty" id="searchEmpty">
                        No courses match that search.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <BadgeDialog
        content={dialogContent}
        open={modalOpen}
        onClose={closeDialog}
      />
    </main>
  );
};

export default NewBadges;
